// modules/freshcards/src/LayoutAPI.ts
//
// 2026-09-03 (Phase 12) — LayoutAPI.
//
// Pure module that owns the layout tokens (columns, spacing,
// typography scale, card geometry) and exposes a small typed surface
// for AI agents to read and write layout values.
//
// Layout state lives in localStorage under `freshcards:layout`. The
// ThemeEngine applies layout tokens as CSS variables on the document
// root. Components already consume those vars (--bg, --text, etc.)
// so the new tokens are pure additions.
//
// This module is PURE: no React, no side effects beyond localStorage.

import { DEFAULT_THEME, type Typography, type CardGeometry } from './ThemeAPI';

// ---------------------------------------------------------------------------
// Layout shape
// ---------------------------------------------------------------------------

export interface Layout {
  /** Page width: 'narrow' (640px), 'standard' (840px), 'wide' (1024px), 'full' (100%). */
  pageWidth: 'narrow' | 'standard' | 'wide' | 'full';
  /** Sidebar position. */
  sidebar: 'left' | 'right' | 'top' | 'hidden';
  /** Page header style. */
  pageHeader: 'compact' | 'standard' | 'hero';
  /** Spacing scale. */
  spacing: 'tight' | 'standard' | 'relaxed';
  /** Density. */
  density: 'comfortable' | 'compact' | 'spacious';
  /** Card geometry (re-export from ThemeAPI for convenience). */
  geometry: CardGeometry;
  /** Typography (re-export from ThemeAPI for convenience). */
  typography: Typography;
}

export const DEFAULT_LAYOUT: Layout = {
  pageWidth: 'standard',
  sidebar: 'left',
  pageHeader: 'standard',
  spacing: 'standard',
  density: 'comfortable',
  geometry: DEFAULT_THEME.geometry,
  typography: DEFAULT_THEME.typography,
};

const LAYOUT_KEY = 'freshcards:layout';

export function loadLayout(): Layout {
  if (typeof localStorage === 'undefined') return DEFAULT_LAYOUT;
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (!raw) return DEFAULT_LAYOUT;
    const parsed = JSON.parse(raw) as Partial<Layout>;
    return { ...DEFAULT_LAYOUT, ...parsed };
  } catch {
    return DEFAULT_LAYOUT;
  }
}

export function saveLayout(layout: Layout): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
}

export function clearLayout(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(LAYOUT_KEY);
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export interface LayoutAPI {
  get(): Layout;
  set(layout: Layout): void;
  reset(): void;
  /** Patch a single field. */
  patch<K extends keyof Layout>(field: K, value: Layout[K]): void;
}

export function makeLayoutAPI(): LayoutAPI {
  return {
    get() { return loadLayout(); },
    set(layout) { saveLayout(layout); },
    reset() { clearLayout(); },
    patch(field, value) {
      const cur = loadLayout();
      saveLayout({ ...cur, [field]: value });
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers — map layout tokens to CSS variable strings
// ---------------------------------------------------------------------------

const PAGE_WIDTH_PX: Record<Layout['pageWidth'], string> = {
  narrow: '640px',
  standard: '840px',
  wide: '1024px',
  full: '100%',
};

const SPACING_PX: Record<Layout['spacing'], number> = {
  tight: 4,
  standard: 8,
  relaxed: 14,
};

const DENSITY_PX: Record<Layout['density'], number> = {
  compact: 6,
  comfortable: 12,
  spacious: 18,
};

export function layoutToCssVars(layout: Layout): Record<string, string> {
  return {
    '--fc-page-max-width': PAGE_WIDTH_PX[layout.pageWidth],
    '--fc-spacing-unit': `${SPACING_PX[layout.spacing]}px`,
    '--fc-density-pad': `${DENSITY_PX[layout.density]}px`,
    '--fc-card-radius': `${layout.geometry.radius}px`,
    '--fc-card-padding': `${layout.geometry.padding}px`,
    '--fc-card-border-width': `${layout.geometry.borderWidth}px`,
    '--fc-base-font-size': `${layout.typography.baseFontSize}px`,
    '--fc-font-family': layout.typography.fontFamily,
    '--fc-heading-font-family': layout.typography.headingFontFamily ?? layout.typography.fontFamily,
  };
}
